// The expense import template — receipts read into structured data (by Claude,
// or anything else that writes this shape) and dropped on Expenses → Import
// receipts, together with the receipt files themselves. Pure: parsing,
// validation and the matching against the app's own lists all live here so
// they are testable without the review screen. The contract for whoever writes
// the file is `.claude/skills/receipt-import.md`.

export const EXPENSE_IMPORT_FORMAT = 'stfors.expense-import';
export const EXPENSE_IMPORT_VERSION = 1;

export interface ExpenseImportLineItem {
  description: string;
  quantity?: string | null;
  /** The line's total, as printed. */
  amount?: string | null;
}

/** One receipt, as the file states it. Strings for money, like the API. */
export interface ExpenseImportItem {
  /** File name of the receipt in the same drop (e.g. "Shell_2026-10-12.pdf"). */
  receipt_file?: string | null;
  /** YYYY-MM-DD — the purchase date on the receipt. */
  date: string;
  vendor?: string | null;
  /** Total paid, tax included. */
  amount: string;
  /** Sales tax portion of `amount`. */
  tax_amount?: string | null;
  receipt_number?: string | null;
  /** An expense_type value from Settings → Lookups. */
  type?: string | null;
  tax_category?: string | null;
  /** Case number of the project, e.g. "STF-2026-TJVORHIS"; null = general business. */
  project_case_number?: string | null;
  reimbursable?: boolean | null;
  mileage?: string | null;
  trip_purpose?: string | null;
  notes?: string | null;
  line_items?: ExpenseImportLineItem[] | null;
  /** Only when the purchase IS a tracked asset (a drive, a laptop). */
  asset?: { name?: string | null; serial_number?: string | null } | null;
}

export interface ExpenseImportFile {
  format: typeof EXPENSE_IMPORT_FORMAT;
  version: number;
  expenses: ExpenseImportItem[];
}

/** A filled-in example — the "Download a sample" link on the import screen. */
export const EXPENSE_IMPORT_SAMPLE: ExpenseImportFile = {
  format: EXPENSE_IMPORT_FORMAT,
  version: EXPENSE_IMPORT_VERSION,
  expenses: [
    {
      receipt_file: 'BestBuy_2026-10-12.pdf',
      date: '2026-10-12',
      vendor: 'Best Buy',
      amount: '129.99',
      tax_amount: '9.10',
      receipt_number: '1234-5678',
      type: 'Equipment',
      tax_category: 'Equipment & Depreciation',
      project_case_number: 'STF-2026-TJVORHIS',
      reimbursable: false,
      mileage: null,
      trip_purpose: null,
      notes: 'Drive for the Sylvetsky production',
      line_items: [{ description: 'WD My Passport 1TB', quantity: '1', amount: '120.89' }],
      asset: { name: 'WD My Passport 1TB', serial_number: 'WX12A3456789' },
    },
    {
      receipt_file: 'Shell_2026-10-13.pdf',
      date: '2026-10-13',
      vendor: 'Gas station',
      amount: '62.40',
      tax_amount: null,
      receipt_number: null,
      type: 'Travel (track mileage)',
      tax_category: null,
      project_case_number: null,
      reimbursable: false,
      mileage: '84.2',
      trip_purpose: 'Drive to client site',
      notes: null,
      line_items: [{ description: 'Unleaded 15.6 gal', quantity: '1', amount: '62.40' }],
      asset: null,
    },
  ],
};

export interface ParsedImport {
  items: ExpenseImportItem[];
  /** Whole-file problems; when non-empty, `items` is empty. */
  fileErrors: string[];
  /** Per-item problems, by index into `items`. An item with errors can't be applied. */
  itemErrors: Record<number, string[]>;
}

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

/** Money as the API wants it: a plain decimal string. Accepts "$1,299.00" and numbers. */
export function normalizeMoney(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v.toFixed(2);
  const s = str(v);
  if (s === null) return null;
  const cleaned = s.replace(/[$,\s]/g, '');
  return MONEY.test(cleaned) ? Number(cleaned).toFixed(2) : null;
}

/** YYYY-MM-DD → local midnight epoch ms, or null when it isn't a real date. */
export function importDateToEpoch(v: string): number | null {
  const m = DATE.exec(v);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt.getTime();
}

/**
 * Parse and check an import file. Never throws: anything wrong comes back as
 * an error to show, so a single bad receipt doesn't sink the rest.
 */
export function parseExpenseImport(raw: unknown): ParsedImport {
  const out: ParsedImport = { items: [], fileErrors: [], itemErrors: {} };
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      out.fileErrors.push('The file is not valid JSON.');
      return out;
    }
  }
  // A bare array of expenses is accepted too — the wrapper only adds a version.
  const obj = Array.isArray(data) ? { format: EXPENSE_IMPORT_FORMAT, version: 1, expenses: data } : data;
  if (typeof obj !== 'object' || obj === null) {
    out.fileErrors.push('Expected an object with an "expenses" list.');
    return out;
  }
  const o = obj as Record<string, unknown>;
  if (o['format'] !== undefined && o['format'] !== EXPENSE_IMPORT_FORMAT) {
    out.fileErrors.push(`Unknown format "${String(o['format'])}" — expected "${EXPENSE_IMPORT_FORMAT}".`);
    return out;
  }
  if (o['version'] !== undefined && Number(o['version']) > EXPENSE_IMPORT_VERSION) {
    out.fileErrors.push(`This file is version ${String(o['version'])}; this app reads up to version ${EXPENSE_IMPORT_VERSION}.`);
    return out;
  }
  const list = o['expenses'];
  if (!Array.isArray(list) || list.length === 0) {
    out.fileErrors.push('The file has no expenses in it.');
    return out;
  }

  list.forEach((entry, i) => {
    const errs: string[] = [];
    const e = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>;
    const date = str(e['date']) ?? '';
    if (importDateToEpoch(date) === null) errs.push(`date "${date}" is not YYYY-MM-DD`);
    const amount = normalizeMoney(e['amount']);
    if (amount === null || Number(amount) <= 0) errs.push('amount (the total paid) is missing or not a number');
    const taxRaw = e['tax_amount'];
    const tax = taxRaw === null || taxRaw === undefined || taxRaw === '' ? null : normalizeMoney(taxRaw);
    if (taxRaw !== null && taxRaw !== undefined && taxRaw !== '' && tax === null) errs.push('tax_amount is not a number');
    if (tax !== null && amount !== null && Number(tax) > Number(amount)) errs.push('tax_amount is more than the total');
    const mileRaw = e['mileage'];
    const mileage = mileRaw === null || mileRaw === undefined || mileRaw === '' ? null : normalizeMoney(mileRaw);
    if (mileRaw !== null && mileRaw !== undefined && mileRaw !== '' && mileage === null) errs.push('mileage is not a number');

    const lines = Array.isArray(e['line_items'])
      ? (e['line_items'] as unknown[])
        .map((l) => (typeof l === 'object' && l !== null ? l as Record<string, unknown> : {}))
        .filter((l) => str(l['description']) !== null)
        .map((l) => ({
          description: str(l['description'])!,
          quantity: str(l['quantity']) ?? (typeof l['quantity'] === 'number' ? String(l['quantity']) : null),
          amount: normalizeMoney(l['amount']),
        }))
      : null;
    const assetRaw = e['asset'];
    const asset = typeof assetRaw === 'object' && assetRaw !== null
      ? {
        name: str((assetRaw as Record<string, unknown>)['name']),
        serial_number: str((assetRaw as Record<string, unknown>)['serial_number']),
      }
      : null;

    out.items.push({
      receipt_file: str(e['receipt_file']),
      date,
      vendor: str(e['vendor']),
      amount: amount ?? '',
      tax_amount: tax,
      receipt_number: str(e['receipt_number']),
      type: str(e['type']),
      tax_category: str(e['tax_category']),
      project_case_number: str(e['project_case_number']),
      reimbursable: e['reimbursable'] === true,
      mileage,
      trip_purpose: str(e['trip_purpose']),
      notes: str(e['notes']),
      line_items: lines && lines.length > 0 ? lines : null,
      asset: asset && (asset.name || asset.serial_number) ? asset : null,
    });
    if (errs.length > 0) out.itemErrors[i] = errs;
  });
  return out;
}

/** The expense's notes: the file's notes, the trip purpose, then the items. */
export function composeImportNotes(item: ExpenseImportItem): string {
  const parts: string[] = [];
  if (item.notes) parts.push(item.notes);
  if (item.trip_purpose) parts.push(`Trip purpose: ${item.trip_purpose}`);
  if (item.line_items && item.line_items.length > 0) {
    parts.push(['Items:', ...item.line_items.map((l) => {
      const qty = l.quantity && l.quantity !== '1' ? ` × ${l.quantity}` : '';
      const amt = l.amount ? ` — $${l.amount}` : '';
      return `- ${l.description}${qty}${amt}`;
    })].join('\n'));
  }
  return parts.join('\n\n');
}

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const stem = (name: string): string => norm(name.replace(/\.[a-z0-9]+$/i, ''));

/** The dropped file a receipt names — exact name first, then ignoring the extension. */
export function matchReceiptFile<F extends { name: string }>(files: F[], wanted: string | null | undefined): F | null {
  if (!wanted) return null;
  const lower = wanted.toLowerCase();
  return files.find((f) => f.name.toLowerCase() === lower)
    ?? files.find((f) => stem(f.name) === stem(wanted))
    ?? null;
}

/** A project by case number, case- and spacing-insensitive. */
export function matchProject<P extends { id: number; case_number: string }>(
  projects: P[], caseNumber: string | null | undefined,
): P | null {
  if (!caseNumber) return null;
  const want = norm(caseNumber);
  return projects.find((p) => norm(p.case_number) === want) ?? null;
}

/** A vendor record by name or DBA. */
export function matchVendor<V extends { id: number; name: string; dba_name?: string | null }>(
  vendors: V[], name: string | null | undefined,
): V | null {
  if (!name) return null;
  const want = norm(name);
  return vendors.find((v) => norm(v.name) === want || (v.dba_name ? norm(v.dba_name) === want : false))
    ?? null;
}

/** A lookup option (expense type, tax category, generic vendor) by value or label. */
export function matchOption<O extends { value: string; label?: string | null }>(
  options: O[], wanted: string | null | undefined,
): O | null {
  if (!wanted) return null;
  const want = norm(wanted);
  return options.find((o) => norm(o.value) === want || (o.label ? norm(o.label) === want : false)) ?? null;
}

/** The tracked asset a purchase is — serial number wins; then an exact name. */
export function matchAsset<A extends { id: number; name: string; serial_number: string | null }>(
  assets: A[], hint: ExpenseImportItem['asset'],
): A | null {
  if (!hint) return null;
  if (hint.serial_number) {
    const s = norm(hint.serial_number);
    const bySerial = assets.find((a) => a.serial_number && norm(a.serial_number) === s);
    if (bySerial) return bySerial;
  }
  if (hint.name) {
    const n = norm(hint.name);
    return assets.find((a) => norm(a.name) === n) ?? null;
  }
  return null;
}

/**
 * An expense already recorded for the same day and total — most likely this
 * receipt, imported before. A warning, never a block: two identical coffees on
 * one day are real.
 */
export function findLikelyDuplicate<E extends { id: number; date: number; amount: string; vendor: string | null }>(
  existing: E[], dateEpoch: number | null, amount: string, vendor: string | null,
): E | null {
  if (dateEpoch === null || amount === '') return null;
  const day = new Date(dateEpoch).toDateString();
  return existing.find((e) =>
    new Date(e.date).toDateString() === day
    && Number(e.amount) === Number(amount)
    && (!vendor || !e.vendor || norm(e.vendor) === norm(vendor)),
  ) ?? null;
}
